import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import TelegramLoginButton from '../sentry/TelegramLoginButton';
import {
  fetchTelegramMe,
  loginTelegram,
  logoutTelegram,
  type TelegramAuthPayload,
  type TelegramUser
} from '../../utils/telegramAuth';
import { fetchSentryFeeSwapSummary, type SentryFeeSwapSummary } from '../../utils/sentryBotApi';
import { SolanaWalletProvider } from '../SolanaWalletProvider';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import SentryPriceFooter from '../sentry/SentryPriceFooter';

const DocsContainer = styled.div`
  min-height: 100vh;
  background: #000000;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  position: relative;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  padding-bottom: 84px;
`;

const SENTRY_LOGO_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/solana-logos/Sentry%20floating%20eye.png';
const SENTRY_LAUNCH_MARKET_BUY = 19465720.490301692;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 20px;
  background: rgba(20, 20, 25, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  margin: 24px;
  min-height: 56px;
  gap: 20px;
  flex-wrap: wrap;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 32px;
`;

const HeaderRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const HeaderLogo = styled.img`
  height: 32px;
  width: auto;
  object-fit: contain;
`;

const HeaderStatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
`;

const HeaderStatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;
  text-align: center;
`;

const HeaderStatsDesktop = styled(HeaderStatsRow)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderStatsMobile = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 12px;
    width: auto;
    position: relative;
  }
`;

const HeaderDropdownButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.03);
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;
`;

const HeaderDropdownPanel = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'grid' : 'none')};
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  z-index: 5;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 12px;
  background: rgba(5, 5, 8, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderStatLabel = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const HeaderStatValue = styled.span<{ $tone?: 'green' | 'gold' }>`
  font-size: 16px;
  font-weight: 700;
  color: ${({ $tone }) => {
    if ($tone === 'gold') return '#FFD700';
    if ($tone === 'green') return '#10b981';
    return '#ffffff';
  }};
`;

const HeaderDocsButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 2px;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  transition: all 0.2s ease;
  
  &:hover {
    color: #ffffff;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderDocsButtonMobile = styled(HeaderDocsButton)`
  display: inline-flex;

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

const TelegramSessionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.8);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const TelegramActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletConnectButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 28px;
  min-height: 28px;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIcon = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  object-fit: contain;
`;

const WalletAddress = styled.span`
  font-family: 'Inter', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const DocsContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 0;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.aside`
  background: rgba(0, 0, 0, 0.8);
  border-right: 1px solid rgba(212, 175, 55, 0.15);
  padding: 32px 24px;
  height: calc(100vh - 73px);
  position: sticky;
  top: 73px;
  overflow-y: auto;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(212, 175, 55, 0.3);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(212, 175, 55, 0.5);
  }
  
  @media (max-width: 1024px) {
    position: static;
    height: auto;
    border-right: none;
    border-bottom: 1px solid rgba(212, 175, 55, 0.15);
    padding: 24px 20px;
  }
`;

const SidebarTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 24px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const SidebarSection = styled.div`
  margin-bottom: 28px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SidebarSectionTitle = styled.h4`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding-left: 12px;
`;

const SidebarLink = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: ${(props: { $active: boolean }) => (props.$active ? '600' : '400')};
  color: ${(props: { $active: boolean }) => (props.$active ? '#D4AF37' : 'rgba(255, 255, 255, 0.65)')};
  text-decoration: none;
  padding: 7px 12px;
  border-radius: 6px;
  margin-bottom: 2px;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${(props: { $active: boolean }) => (props.$active ? 'rgba(212, 175, 55, 0.15)' : 'transparent')};
  border-left: ${(props: { $active: boolean }) => (props.$active ? '2px solid #D4AF37' : '2px solid transparent')};
  
  &:hover {
    color: #D4AF37;
    background: rgba(212, 175, 55, 0.1);
    text-decoration: none;
    transform: translateX(2px);
  }
`;

const MainContent = styled.main`
  background: transparent;
  padding: 48px 64px;
  min-height: calc(100vh - 73px);
  max-width: 900px;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  
  /* Ensure text wraps properly on mobile */
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  /* Override link colors to gold */
  a {
    color: #D4AF37 !important;
    word-break: break-word;
    
    &:hover {
      color: #F4D03F !important;
    }
  }
  
  /* Override code block borders to gold */
  code {
    border-color: rgba(212, 175, 55, 0.3) !important;
    background: rgba(212, 175, 55, 0.1) !important;
    color: #F4D03F !important;
    word-break: break-word;
    white-space: pre-wrap;
  }
  
  /* Override list markers to gold */
  li::marker {
    color: rgba(212, 175, 55, 0.7) !important;
  }
  
  /* Override blockquote to gold */
  blockquote {
    border-left-color: rgba(212, 175, 55, 0.5) !important;
  }
  
  /* Override any purple gradients to gold */
  * {
    &[style*="168, 85, 247"],
    &[style*="a855f7"],
    &[style*="9333ea"] {
      background: linear-gradient(135deg, rgba(212, 175, 55, 0.9), rgba(244, 208, 63, 0.9)) !important;
    }
  }
  
  /* Ensure tables and code blocks are scrollable on mobile */
  table {
    width: 100%;
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  pre {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    word-wrap: normal;
    white-space: pre;
  }
  
  @media (max-width: 1024px) {
    padding: 40px 32px;
  }
  
  @media (max-width: 768px) {
    padding: 32px 24px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 24px 16px;
    max-width: 100%;
  }
`;

const HeaderError = styled.div`
  margin: 0 24px 16px;
  padding: 12px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
`;

const SentryDocsLayoutContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { connected, publicKey, wallet } = useWallet();
  const { setVisible } = useWalletModal();
  const [feeSwapSummary, setFeeSwapSummary] = useState<SentryFeeSwapSummary | null>(null);
  const [feeSwapSummaryError, setFeeSwapSummaryError] = useState<string | null>(null);
  const [buybacksOpen, setBuybacksOpen] = useState(false);
  const [telegramUser, setTelegramUser] = useState<TelegramUser | null>(null);
  const [telegramAuthError, setTelegramAuthError] = useState<string | null>(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const telegramBotUsername = (import.meta.env as any).VITE_TELEGRAM_BOT_USERNAME as string | undefined;

  const sidebarSections = [
    {
      title: 'Getting Started',
      links: [
        { path: '/sentry/docs', label: 'Introduction', exact: true },
        { path: '/sentry/docs/getting-started', label: 'Getting Started Intro' },
        { path: '/sentry/docs/quick-start', label: 'Quick Start Guide' }
      ]
    },
    {
      title: 'Group Setup',
      links: [
        { path: '/sentry/docs/group-setup', label: 'Overview' },
        { path: '/sentry/docs/group-setup/initial-setup', label: 'Initial Setup' },
        { path: '/sentry/docs/group-setup/traders', label: 'Traders' },
        { path: '/sentry/docs/group-setup/wallets', label: 'Wallets' },
        { path: '/sentry/docs/group-setup/payout-wallets', label: 'Payout Wallets' },
        { path: '/sentry/docs/group-setup/settings', label: 'Settings' }
      ]
    },
    {
      title: 'Trading',
      links: [
        { path: '/sentry/docs/trading', label: 'Overview' },
        { path: '/sentry/docs/trading/calling-tokens', label: 'Calling Tokens' },
        { path: '/sentry/docs/trading/dca', label: 'DCA' },
        { path: '/sentry/docs/trading/viewing-positions', label: 'Viewing Positions' },
        { path: '/sentry/docs/trading/selling-positions', label: 'Selling Positions' },
        { path: '/sentry/docs/trading/take-profit', label: 'Take Profit' }
      ]
    },
    {
      title: 'Analytics',
      links: [
        { path: '/sentry/docs/analytics', label: 'Overview' },
        { path: '/sentry/docs/analytics/balance', label: 'Balance' },
        { path: '/sentry/docs/analytics/stats', label: 'Stats' },
        { path: '/sentry/docs/analytics/leaderboard', label: 'Leaderboard' },
        { path: '/sentry/docs/analytics/pnl', label: 'PnL' }
      ]
    },
    {
      title: 'Deployment',
      links: [
        { path: '/sentry/docs/deployment', label: 'Overview' },
        { path: '/sentry/docs/deployment/deploy-wizard', label: 'Deploy Wizard' },
        { path: '/sentry/docs/deployment/deployer-menu', label: 'Deployer Menu' },
        { path: '/sentry/docs/deployment/bundle-wallets', label: 'Bundle Wallets' },
        { path: '/sentry/docs/deployment/bundle-sell', label: 'Bundle Sell' },
        { path: '/sentry/docs/deployment/bundle-withdraw-harvest', label: 'Bundle Withdraw' }
      ]
    },
    {
      title: 'Menu Reference',
      links: [
        { path: '/sentry/docs/menu-reference', label: 'Overview' }
      ]
    }
  ];

  const isActive = (path: string, exact: boolean = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const sentryBuyBacks = feeSwapSummary
    ? Math.max(0, feeSwapSummary.totalSentryBought - SENTRY_LAUNCH_MARKET_BUY)
    : null;
  const sentryBuyBacksUsd = sentryBuyBacks !== null && feeSwapSummary
    ? sentryBuyBacks * feeSwapSummary.sentryPriceUsd
    : null;
  const headerFeeStats = [
    {
      label: 'Sentry Bot Trades',
      value: feeSwapSummary ? feeSwapSummary.totalBuys.toLocaleString('en-US') : '--',
      tone: 'green' as const
    },
    {
      label: '$SENTRY Buy Backs',
      value: sentryBuyBacks !== null ? sentryBuyBacks.toLocaleString('en-US') : '--',
      tone: 'gold' as const
    },
    {
      label: 'USD Value',
      value: sentryBuyBacksUsd !== null ? `$${sentryBuyBacksUsd.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '--',
      tone: 'green' as const
    }
  ];

  const walletLabel = connected && publicKey
    ? `${publicKey.toBase58().slice(0, 4)}...${publicKey.toBase58().slice(-4)}`
    : 'Connect Wallet';
  const walletIcon = connected ? wallet?.adapter?.icon : null;

  const handleTelegramAuth = async (payload: TelegramAuthPayload) => {
    setTelegramLoading(true);
    setTelegramAuthError(null);
    try {
      const user = await loginTelegram(payload);
      setTelegramUser(user);
    } catch (error: any) {
      setTelegramAuthError(error?.message || 'Telegram login failed.');
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTelegramLogout = async () => {
    setTelegramLoading(true);
    setTelegramAuthError(null);
    try {
      await logoutTelegram();
      setTelegramUser(null);
    } catch (error: any) {
      setTelegramAuthError(error?.message || 'Failed to disconnect Telegram.');
    } finally {
      setTelegramLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadTelegramUser = async () => {
      try {
        const user = await fetchTelegramMe();
        if (!cancelled) {
          setTelegramUser(user);
        }
      } catch (error) {
        if (!cancelled) {
          setTelegramUser(null);
        }
      }
    };

    loadTelegramUser();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const fetchSummary = async () => {
      try {
        setFeeSwapSummaryError(null);
        const summary = await fetchSentryFeeSwapSummary();
        if (cancelled) return;
        if (summary) {
          setFeeSwapSummary(summary);
        } else if (!feeSwapSummary) {
          setFeeSwapSummaryError('Swap summary unavailable');
        }
      } catch (error) {
        if (!cancelled && !feeSwapSummary) {
          setFeeSwapSummaryError('Swap summary unavailable');
        }
      } finally {
        if (cancelled) return;
        const shouldPollFast = feeSwapSummary?.truncated || false;
        const nextDelayMs = shouldPollFast ? 60 * 1000 : 30 * 60 * 1000;
        timeoutId = setTimeout(fetchSummary, nextDelayMs);
      }
    };

    fetchSummary();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [feeSwapSummary?.truncated]);

  return (
    <DocsContainer>
      <HeaderBar>
          <HeaderLeft>
            <HeaderLogo src={SENTRY_LOGO_URL} alt="Sentry" />
          </HeaderLeft>
          <HeaderStatsDesktop>
            {headerFeeStats.map((stat) => (
              <HeaderStatItem key={stat.label}>
                <HeaderStatLabel>{stat.label}</HeaderStatLabel>
                <HeaderStatValue $tone={stat.tone}>{stat.value}</HeaderStatValue>
              </HeaderStatItem>
            ))}
          </HeaderStatsDesktop>
          <HeaderDocsButton href="https://sentry.trading/dashboard">
            Dashboard
          </HeaderDocsButton>
          <HeaderDocsButton href="https://sentry.trading/tokens">
            Tokens
          </HeaderDocsButton>
          <HeaderDocsButton href="https://sentry.trading/docs" target="_blank" rel="noopener noreferrer">
            Docs
          </HeaderDocsButton>
          <HeaderStatsMobile>
            <HeaderDropdownButton onClick={() => setBuybacksOpen((prev) => !prev)}>
              Buy Backs
              <span>{buybacksOpen ? '−' : '+'}</span>
            </HeaderDropdownButton>
            <HeaderDocsButtonMobile href="https://sentry.trading/dashboard">
              Dashboard
            </HeaderDocsButtonMobile>
            <HeaderDocsButtonMobile href="https://sentry.trading/tokens">
              Tokens
            </HeaderDocsButtonMobile>
            <HeaderDocsButtonMobile href="https://sentry.trading/docs" target="_blank" rel="noopener noreferrer">
              Docs
            </HeaderDocsButtonMobile>
            <HeaderDropdownPanel $open={buybacksOpen}>
              {headerFeeStats.map((stat) => (
                <HeaderStatItem key={`mobile-${stat.label}`}>
                  <HeaderStatLabel>{stat.label}</HeaderStatLabel>
                  <HeaderStatValue $tone={stat.tone}>{stat.value}</HeaderStatValue>
                </HeaderStatItem>
              ))}
            </HeaderDropdownPanel>
          </HeaderStatsMobile>
          <HeaderRight>
            <WalletConnectButton type="button" onClick={() => setVisible(true)}>
              {walletIcon && <WalletIcon src={walletIcon} alt={`${wallet?.adapter?.name || 'Wallet'} logo`} />}
              {connected ? <WalletAddress>{walletLabel}</WalletAddress> : walletLabel}
            </WalletConnectButton>
            {telegramUser ? (
              <>
                <TelegramSessionBadge>
                  Connected{telegramUser.username ? ` @${telegramUser.username}` : ''}
                </TelegramSessionBadge>
                <TelegramActionButton onClick={handleTelegramLogout} disabled={telegramLoading}>
                  Disconnect
                </TelegramActionButton>
              </>
            ) : (
              <TelegramLoginButton botUsername={telegramBotUsername} onAuth={handleTelegramAuth} label="Connect" />
            )}
          </HeaderRight>
        </HeaderBar>
      {telegramAuthError && <HeaderError>{telegramAuthError}</HeaderError>}
      {feeSwapSummaryError && <HeaderError>{feeSwapSummaryError}</HeaderError>}

      <DocsContent>
        <Sidebar>
          <SidebarTitle>Contents</SidebarTitle>
          {sidebarSections.map((section, index) => (
            <SidebarSection key={index}>
              <SidebarSectionTitle>{section.title}</SidebarSectionTitle>
              {section.links.map((link, linkIndex) => (
                <SidebarLink
                  key={linkIndex}
                  to={link.path}
                  $active={isActive(link.path, link.exact)}
                >
                  {link.label}
                </SidebarLink>
              ))}
            </SidebarSection>
          ))}
        </Sidebar>

        <MainContent>
          {children}
        </MainContent>
      </DocsContent>
      <SentryPriceFooter />
    </DocsContainer>
  );
};

const SentryDocsLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SolanaWalletProvider>
    <SentryDocsLayoutContent>{children}</SentryDocsLayoutContent>
  </SolanaWalletProvider>
);

export default SentryDocsLayout;
